(() => {
  const SELECTOR = '[data-goodlot-recommendations]';

  const shuffle = (items) => {
    const shuffled = [...items];

    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
    }

    return shuffled;
  };


  const getCardProductId = (card) =>
    String(card?.dataset.goodlotProductId || '').trim();

  const getCardPrice = (card) => {
    const price = Number(card?.dataset.goodlotPrice || 0);
    return Number.isFinite(price) ? price : 0;
  };

  const uniqueCards = (cards) => {
    const seen = new Set();

    return cards.filter((card) => {
      const id = getCardProductId(card);

      if (!id || seen.has(id)) return false;

      seen.add(id);
      return true;
    });
  };

  /*
   * Smart same-category ordering:
   * - Products at / above the viewed product price are ordered from the closest upgrade upward.
   * - Products below the viewed product price are ordered from the closest saving downward.
   * - We alternate premium / value so a higher-priced option is followed by a cheaper one.
   * - If one side runs out, the remaining products are filled by price proximity.
   */
  const buildPriceBalancedSimilar = (cards, currentPrice) => {
    const candidates = uniqueCards(cards);

    if (candidates.length <= 1) return candidates;

    const alternateHighLow = (sourceCards) => {
      const sorted = [...sourceCards].sort((a, b) => getCardPrice(a) - getCardPrice(b));
      const balanced = [];
      let low = 0;
      let high = sorted.length - 1;

      while (low <= high) {
        if (high >= low) balanced.push(sorted[high]);
        high -= 1;

        if (low <= high) balanced.push(sorted[low]);
        low += 1;
      }

      return balanced;
    };

    if (!Number.isFinite(currentPrice) || currentPrice <= 0) {
      return alternateHighLow(candidates);
    }

    const premium = candidates
      .filter((card) => getCardPrice(card) >= currentPrice)
      .sort((a, b) => getCardPrice(a) - getCardPrice(b));

    const value = candidates
      .filter((card) => getCardPrice(card) < currentPrice)
      .sort((a, b) => getCardPrice(b) - getCardPrice(a));

    /*
     * When we have products on both sides of the viewed price,
     * alternate nearest upgrade / nearest saving.
     * If everything is only cheaper (or only more expensive),
     * alternate the highest and lowest prices instead so the carousel
     * still feels intentionally price-diverse rather than monotonically sorted.
     */
    if (premium.length === 0 || value.length === 0) {
      return alternateHighLow(candidates);
    }

    const balanced = [];
    let premiumIndex = 0;
    let valueIndex = 0;

    while (premiumIndex < premium.length || valueIndex < value.length) {
      if (premiumIndex < premium.length) {
        balanced.push(premium[premiumIndex]);
        premiumIndex += 1;
      }

      if (valueIndex < value.length) {
        balanced.push(value[valueIndex]);
        valueIndex += 1;
      }
    }

    return balanced;
  };

  const buildSmartCardSelection = (root, allCards) => {
    const displayCount = Math.max(1, Number(root.dataset.goodlotDisplayCount || 10));
    const similarTarget = Math.max(
      0,
      Math.min(displayCount, Number(root.dataset.goodlotSimilarCount || 6))
    );
    const randomTarget = Math.max(
      0,
      Math.min(displayCount - similarTarget, Number(root.dataset.goodlotRandomCount || 4))
    );

    const currentProductId = String(root.dataset.goodlotCurrentProductId || '').trim();
    const currentPrice = Number(root.dataset.goodlotCurrentPrice || 0);

    const cards = uniqueCards(
      allCards.filter((card) => getCardProductId(card) !== currentProductId)
    );

    const similarPool = cards.filter(
      (card) => card.dataset.goodlotRecommendationSource === 'similar'
    );

    const relatedPool = cards.filter(
      (card) => card.dataset.goodlotRecommendationSource === 'related'
    );

    const priceBalancedSimilar = buildPriceBalancedSimilar(similarPool, currentPrice);
    const selectedSimilar = priceBalancedSimilar.slice(0, similarTarget);

    const usedIds = new Set(selectedSimilar.map(getCardProductId));

    const discoveryPool = relatedPool.filter(
      (card) => !usedIds.has(getCardProductId(card))
    );

    const selectedRandom = shuffle(discoveryPool).slice(0, randomTarget);

    selectedRandom.forEach((card) => usedIds.add(getCardProductId(card)));

    const selected = [...selectedSimilar, ...selectedRandom];

    /*
     * Fallback:
     * If Shopify's related pool doesn't contain enough unique products,
     * fill the remaining slots from unused same-group candidates first,
     * then from any remaining unique candidate. This keeps the carousel
     * as close to 10 products as the available catalog allows.
     */
    if (selected.length < displayCount) {
      const fallbackCandidates = [
        ...priceBalancedSimilar.slice(selectedSimilar.length),
        ...shuffle(relatedPool),
        ...cards,
      ];

      fallbackCandidates.forEach((card) => {
        if (selected.length >= displayCount) return;

        const id = getCardProductId(card);
        if (!id || usedIds.has(id)) return;

        usedIds.add(id);
        selected.push(card);
      });
    }

    return selected.slice(0, displayCount);
  };

  const setCardError = (form, message = '') => {
    const error = form.querySelector('[data-goodlot-recommendation-error]');
    if (!error) return;

    error.textContent = message;
    error.hidden = !message;
  };

  const initForms = (root) => {
    const forms = [...root.querySelectorAll('[data-goodlot-recommendation-form]')];

    forms.forEach((form) => {
      if (form.dataset.goodlotRecommendationFormInitialized === 'true') return;
      form.dataset.goodlotRecommendationFormInitialized = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        setCardError(form);

        const variantInput = form.querySelector('input[name="id"]');
        const quantityInput = form.querySelector('input[name="quantity"]');
        const button = form.querySelector('button[type="submit"]');

        const variantId = variantInput?.value;
        const quantity = Math.max(1, Number(quantityInput?.value || 1));

        if (!variantId) {
          setCardError(form, 'This option is currently unavailable.');
          return;
        }

        const actions = window.Shopify?.actions;

        if (!actions?.updateCart) {
          HTMLFormElement.prototype.submit.call(form);
          return;
        }

        if (button) {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        }

        try {
          const result = await actions.updateCart({
            lines: [
              {
                merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
                quantity,
              },
            ],
          });

          if (result?.userErrors?.length) {
            const message =
              result.userErrors.find((error) => error?.message)?.message ||
              'This item could not be added to your cart.';
            setCardError(form, message);
          }

          /*
           * Deliberately do not call openCart() / drawer.open() here.
           * GoodLot's existing Shopify Standard Action + cart drawer lifecycle
           * remains the single owner of cart updates and drawer opening.
           */
        } catch (error) {
          console.error('[goodlot-recommendations] Add to cart failed:', error);
          setCardError(form, 'Unable to add this item right now. Please try again.');
        } finally {
          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }
        }
      });
    });
  };

  const buildDots = (root, cards, viewport, getCardScrollLeft) => {
    const dotsContainer = root.querySelector('[data-goodlot-recommendations-dots]');
    if (!dotsContainer) return [];

    dotsContainer.replaceChildren();

    return cards.map((card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `goodlot-recommendations__dot${index === 0 ? ' is-active' : ''}`;
      dot.dataset.goodlotRecommendationsDot = String(index);
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Show recommended product ${index + 1}`);
      dot.setAttribute('aria-selected', index === 0 ? 'true' : 'false');

      dot.addEventListener('click', () => {
        viewport.scrollTo({
          left: getCardScrollLeft(card),
          behavior: 'smooth',
        });
      });

      dotsContainer.appendChild(dot);
      return dot;
    });
  };

  const initCarousel = (root) => {
    if (root.dataset.goodlotRecommendationsInitialized === 'true') return;

    const viewport = root.querySelector('[data-goodlot-recommendations-viewport]');
    const track = root.querySelector('[data-goodlot-recommendations-track]');
    const carousel = root.querySelector('[data-goodlot-recommendations-carousel]');

    if (!viewport || !track || !carousel) return;

    const allCards = [...track.querySelectorAll('[data-goodlot-recommendation-card]')];
    const cards = buildSmartCardSelection(root, allCards);

    allCards.forEach((card) => card.remove());
    cards.forEach((card) => track.appendChild(card));
    if (cards.length === 0) {
      root.hidden = true;
      return;
    }

    root.dataset.goodlotRecommendationsInitialized = 'true';

    const getCardScrollLeft = (card) => {
      const viewportRect = viewport.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const rawPosition = cardRect.left - viewportRect.left + viewport.scrollLeft;
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      return Math.min(Math.max(0, rawPosition), maxScroll);
    };

    const dots = buildDots(root, cards, viewport, getCardScrollLeft);

    const setActive = (index) => {
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    const updateEdgeState = () => {
      const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      carousel.classList.toggle('is-at-end', maxScroll - viewport.scrollLeft <= 2);
    };

    const updateActiveCard = () => {
      if (cards.length === 0) return;

      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card, index) => {
        const distance = Math.abs(getCardScrollLeft(card) - viewport.scrollLeft);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setActive(closestIndex);
      updateEdgeState();
    };

    let scrollFrame = 0;
    viewport.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(updateActiveCard);
      },
      { passive: true }
    );

    window.addEventListener(
      'resize',
      () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(updateActiveCard);
      },
      { passive: true }
    );

    initForms(root);
    updateActiveCard();
  };

  const loadRecommendations = async (root) => {
    if (root.dataset.goodlotRecommendationsLoading === 'true') return;

    const hasServerRenderedCards =
      root.querySelectorAll('[data-goodlot-recommendation-card]').length > 0;

    if (hasServerRenderedCards) {
      initCarousel(root);
      return;
    }

    const url = root.dataset.goodlotRecommendationsUrl;
    if (!url) {
      root.hidden = true;
      return;
    }

    root.dataset.goodlotRecommendationsLoading = 'true';

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`Recommendations request failed: ${response.status}`);
      }

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const responseRoot = documentFragment.querySelector(SELECTOR);

      if (!responseRoot) {
        root.hidden = true;
        return;
      }

      const responseCards = responseRoot.querySelectorAll('[data-goodlot-recommendation-card]');
      if (responseCards.length === 0) {
        root.hidden = true;
        return;
      }

      root.innerHTML = responseRoot.innerHTML;
      root.dataset.goodlotRecommendationsLoading = 'false';

      initCarousel(root);
    } catch (error) {
      console.warn('[goodlot-recommendations] Unable to load recommendations:', error);
      root.hidden = true;
    }
  };

  const observeRoot = (root) => {
    if (!('IntersectionObserver' in window)) {
      loadRecommendations(root);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        observer.disconnect();
        loadRecommendations(root);
      },
      {
        rootMargin: '500px 0px',
        threshold: 0,
      }
    );

    observer.observe(root);
  };

  const placeAfterPriceProof = (root) => {
  if (!root) return;

  const productHighlights = document.querySelector(
    '.goodlot-product-highlights'
  );

  /*
   * Place "You may also like" directly AFTER
   * "Why customers love it".
   */
  if (productHighlights) {
    if (productHighlights.nextElementSibling !== root) {
      productHighlights.insertAdjacentElement('afterend', root);
    }
  }
};

  const init = (scope = document) => {
    scope.querySelectorAll?.(SELECTOR).forEach((root) => {
      placeAfterPriceProof(root);
      observeRoot(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector?.(SELECTOR);

    if (root) {
      placeAfterPriceProof(root);
      observeRoot(root);
    }
  });
})();
